import { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, AlertTriangle, Activity, Map, Brain } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.15 (Tier 1 greenfield, per docs/BUILD_ROADMAP.md): Risk Engine (Module 6).
// Surfaces GET /api/risk (riskService.buildRiskDashboard): one transparent risk posture
// aggregated from market tail risk + portfolio beta + concentration + scenario worst-case +
// behavioral journal. Rule-based, reasoning chain shown (no black boxes).

const LEVEL_COLOR = { calm: '#10b981', watching: '#f59e0b', elevated: '#f97316', crisis: '#ef4444' };
const MUTED = '#6b7280';

function levelColor(l) { return LEVEL_COLOR[String(l || '').toLowerCase()] || MUTED; }

function pctColor(p) { return p == null ? MUTED : p < 0 ? '#ef4444' : '#10b981'; }

function fmtPct(p) { return p == null ? '-' : `${p > 0 ? '+' : ''}${p}%`; }

function Badge({ label, color }) {
  return (
    <span
      className="text-[10px] font-mono font-bold px-2 py-0.5 rounded uppercase tracking-wide"
      style={{ color: color || MUTED, backgroundColor: `${color || MUTED}1f` }}
    >
      {label}
    </span>
  );
}

export default function RiskEngine() {
  const { setPage } = usePage();
  useEffect(() => { setPage('risk'); }, []);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch('/api/risk');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const posture = data?.posture || null;
  const dims = data?.dimensions || {};
  const tail = dims.market_tail_risk;
  const beta = dims.portfolio_beta;
  const conc = dims.concentration;
  const scen = dims.scenario_worst_case;
  const behav = dims.behavioral;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Shield className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Risk Engine</h1>
        <span className="text-korinth-muted text-sm">Aggregated risk posture across market, portfolio, scenario, and behavioral dimensions</span>
        <button
          onClick={load}
          className="ml-auto flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-border/40 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}

      {/* Posture banner */}
      {posture && (
        <div
          className="rounded-lg border p-4"
          style={{ borderColor: `${levelColor(posture.level)}66`, backgroundColor: `${levelColor(posture.level)}12` }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <AlertTriangle className="w-5 h-5" style={{ color: levelColor(posture.level) }} />
            <span className="text-xl font-bold uppercase tracking-wide" style={{ color: levelColor(posture.level) }}>
              {posture.level}
            </span>
            <span className="text-korinth-muted text-xs font-mono">severity {posture.severity}/3</span>
          </div>
          {posture.reasons?.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {posture.reasons.map((r, i) => (
                <li key={i} className="text-[11px] font-mono text-korinth-text">
                  <span className="text-korinth-muted">{r.dimension}</span>: {r.why} <span className="text-korinth-muted">(sev {r.severity})</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-2 text-[11px] font-mono text-korinth-muted">No dimension is elevated. {posture.method}</div>
          )}
        </div>
      )}

      {loading && !data && <div className="text-[11px] text-korinth-muted">Loading risk posture...</div>}

      {/* Dimension grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Market tail risk */}
        <ModuleContainer title="Market tail risk" subtitle="Composite market-stress score (tailRiskService)">
          {!tail || tail.ok === false ? (
            <div className="text-[11px] text-korinth-muted">{tail?.error || 'No data.'}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold" style={{ color: levelColor(tail.stress_level) }}>{tail.score}<span className="text-base text-korinth-muted">/100</span></div>
                <Badge label={tail.stress_level} color={levelColor(tail.stress_level)} />
              </div>
              {tail.firing_signals?.length > 0 ? (
                <ul className="space-y-1">
                  {tail.firing_signals.map((s, i) => (
                    <li key={i} className="text-[11px] font-mono text-korinth-text flex items-start gap-1.5">
                      <Activity className="w-3 h-3 mt-0.5 shrink-0 text-korinth-muted" />{s}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[11px] text-korinth-muted">No tail-risk signals firing.</div>
              )}
            </div>
          )}
        </ModuleContainer>

        {/* Portfolio beta */}
        <ModuleContainer title="Portfolio beta" subtitle="Equity beta vs SPY per account (portfolioBetaService)">
          {!beta || beta.ok === false ? (
            <div className="text-[11px] text-korinth-muted">{beta?.error || 'No data.'}</div>
          ) : beta.accounts?.length === 0 ? (
            <div className="text-[11px] text-korinth-muted">{beta.note || 'No cached beta yet.'}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold text-white">{beta.blended_beta ?? '-'}</div>
                <span className="text-[10px] text-korinth-muted font-mono">blended ({beta.blend_method})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] font-mono">
                  <thead>
                    <tr className="text-korinth-muted border-b border-korinth-border/30">
                      <th className="text-left py-1 px-2">Account</th>
                      <th className="text-right py-1 px-2">Beta</th>
                      <th className="text-right py-1 px-2">Incl/Excl</th>
                      <th className="text-left py-1 px-2">Complete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beta.accounts.map((a) => (
                      <tr key={a.account_id} className="border-b border-korinth-border/10">
                        <td className="py-1 px-2 text-white">{a.broker} #{a.account_id}</td>
                        <td className="py-1 px-2 text-right text-korinth-text">{a.portfolio_beta}</td>
                        <td className="py-1 px-2 text-right text-korinth-muted">{a.tickers_included?.length ?? 0}/{a.tickers_excluded?.length ?? 0}</td>
                        <td className="py-1 px-2">{a.price_data_complete ? <span className="text-green-400">yes</span> : <span className="text-amber-400">partial</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModuleContainer>

        {/* Concentration */}
        <ModuleContainer title="Concentration" subtitle="Sector exposure + alerts (portfolio_exposure_summary)">
          {!conc || conc.ok === false ? (
            <div className="text-[11px] text-korinth-muted">{conc?.error || 'No data.'}</div>
          ) : !conc.available ? (
            <div className="text-[11px] text-korinth-muted">{conc.note}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap text-[11px] font-mono">
                <span className="text-korinth-muted">Top sector</span>
                <span className="text-white font-bold">{conc.top_sector ?? '-'}</span>
                <span style={{ color: conc.top_sector_pct > 40 ? '#f97316' : '#e5e7eb' }}>{conc.top_sector_pct}%</span>
                <span className="text-korinth-muted">· {conc.total_position_count} positions</span>
              </div>
              {Object.keys(conc.sector_breakdown || {}).length > 0 && (
                <div className="space-y-1">
                  {Object.entries(conc.sector_breakdown).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([sec, pct]) => (
                    <div key={sec} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-korinth-muted w-28 truncate">{sec}</span>
                      <div className="flex-1 h-2 rounded bg-korinth-bg/60 overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: '#3b82f6' }} />
                      </div>
                      <span className="text-[10px] font-mono text-korinth-text w-10 text-right">{pct}%</span>
                    </div>
                  ))}
                </div>
              )}
              {conc.concentration_alerts?.length > 0 && (
                <div className="space-y-1">
                  {conc.concentration_alerts.map((al, i) => (
                    <div key={i} className="text-[11px] font-mono text-amber-400 flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{typeof al === 'string' ? al : JSON.stringify(al)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModuleContainer>

        {/* Scenario worst-case */}
        <ModuleContainer title="Scenario worst-case" subtitle="Shadow-portfolio stress across scenarios (scenarioService)">
          {!scen || scen.ok === false ? (
            <div className="text-[11px] text-korinth-muted">{scen?.error || 'No data.'}</div>
          ) : !scen.available ? (
            <div className="text-[11px] text-korinth-muted">{scen.note}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Map className="w-4 h-4 text-korinth-muted" />
                <span className="text-sm text-white font-semibold">{scen.worst?.scenario}</span>
                <span className="text-xl font-bold" style={{ color: pctColor(scen.worst?.total_pnl_pct) }}>{fmtPct(scen.worst?.total_pnl_pct)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] font-mono">
                  <thead>
                    <tr className="text-korinth-muted border-b border-korinth-border/30">
                      <th className="text-left py-1 px-2">Scenario</th>
                      <th className="text-right py-1 px-2">P&amp;L %</th>
                      <th className="text-right py-1 px-2">Affected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scen.all.map((s) => (
                      <tr key={s.id} className="border-b border-korinth-border/10">
                        <td className="py-1 px-2 text-korinth-text">{s.scenario}</td>
                        <td className="py-1 px-2 text-right font-bold" style={{ color: pctColor(s.total_pnl_pct) }}>{fmtPct(s.total_pnl_pct)}</td>
                        <td className="py-1 px-2 text-right text-korinth-muted">{s.positions_affected}/{s.positions_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModuleContainer>
      </div>

      {/* Behavioral */}
      <ModuleContainer title="Behavioral" subtitle="Decision journal (v1 surfaces; scoring is a later increment)">
        {!behav || behav.ok === false ? (
          <div className="text-[11px] text-korinth-muted">{behav?.error || 'No data.'}</div>
        ) : behav.status === 'no_data' ? (
          <div className="text-[11px] text-korinth-muted flex items-center gap-2"><Brain className="w-3.5 h-3.5" />{behav.note}</div>
        ) : (
          <div className="space-y-3">
            <div className="text-[11px] font-mono text-korinth-text">{behav.decisions_90d} decisions logged (90d)</div>
            <div className="flex flex-wrap gap-6">
              {behav.by_action?.length > 0 && (
                <div>
                  <div className="text-[10px] text-korinth-muted uppercase tracking-wide mb-1">By action</div>
                  {behav.by_action.map((a) => (
                    <div key={a.action} className="text-[11px] font-mono text-korinth-text flex gap-2"><span className="w-20 truncate">{a.action}</span><span className="text-korinth-muted">{a.count}</span></div>
                  ))}
                </div>
              )}
              {behav.by_emotion?.length > 0 && (
                <div>
                  <div className="text-[10px] text-korinth-muted uppercase tracking-wide mb-1">By emotional state</div>
                  {behav.by_emotion.map((e) => (
                    <div key={e.state} className="text-[11px] font-mono text-korinth-text flex gap-2"><span className="w-20 truncate">{e.state}</span><span className="text-korinth-muted">{e.count}</span></div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-[10px] text-korinth-muted">{behav.note}</div>
          </div>
        )}
      </ModuleContainer>

      {data?.as_of && (
        <div className="text-[10px] text-korinth-muted font-mono">Posture as of {new Date(data.as_of).toLocaleString()} · {posture?.method} · auto-refresh 120s</div>
      )}
    </div>
  );
}
